'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Native playback belongs to the current visible view, never to a detached node. */
export function useVideoElement({ active = true }: { active?: boolean } = {}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const playbackRevision = useRef(0);
  const [autoPlayAllowed, setAutoPlayAllowed] = useState(true);
  const removeListeners = useRef<(() => void) | null>(null);
  const released = useRef<{
    element: HTMLVideoElement;
    src: string | null;
    srcObject: HTMLVideoElement['srcObject'];
    autoPlay: boolean;
    muted: boolean;
  } | null>(null);

  const isPlaybackAllowed = useCallback(() => {
    const element = video.current;
    return Boolean(element?.isConnected && activeRef.current && element.ownerDocument.visibilityState !== 'hidden');
  }, []);

  const stop = useCallback(() => {
    playbackRevision.current++;
    setAutoPlayAllowed(false);
    const element = video.current;
    if (element) { element.autoplay = false; element.pause(); }
  }, []);

  const videoRef = useCallback((element: HTMLVideoElement | null) => {
    const previous = video.current;
    removeListeners.current?.();
    removeListeners.current = null;
    if (previous && previous !== element) {
      playbackRevision.current++;
      released.current = {
        element: previous, src: previous.getAttribute('src'), srcObject: previous.srcObject,
        autoPlay: previous.autoplay, muted: previous.muted,
      };
      // Removing a DOM node alone leaves its native player and pending play alive.
      previous.autoplay = false;
      previous.muted = true;
      previous.pause();
      previous.removeAttribute('src');
      if (previous.srcObject) previous.srcObject = null;
      previous.load();
    }
    video.current = element;
    const restore = released.current;
    released.current = element ? null : released.current;
    // Strict Mode reattaches the same node after replaying ref cleanup.
    if (element && restore?.element === element) {
      element.muted = restore.muted;
      element.autoplay = restore.autoPlay;
      if (restore.src !== null) element.setAttribute('src', restore.src);
      if (restore.srcObject) element.srcObject = restore.srcObject;
    }
    if (!element) return;
    const doc = element.ownerDocument;
    const claimAudio = () => {
      if (!isPlaybackAllowed()) { stop(); return; }
      if (element.paused || element.muted || element.volume === 0) return;
      // Also covers native controls enabling sound on the selected reference.
      doc.querySelectorAll('video, audio').forEach(other => {
        const media = other as HTMLMediaElement;
        if (media !== element && !media.paused && !media.muted && media.volume > 0) media.pause();
      });
    };
    const paused = () => { playbackRevision.current++; setAutoPlayAllowed(false); };
    const hidden = () => { if (doc.visibilityState === 'hidden') stop(); };
    element.addEventListener('play', claimAudio);
    element.addEventListener('playing', claimAudio);
    element.addEventListener('volumechange', claimAudio);
    element.addEventListener('pause', paused);
    doc.addEventListener('visibilitychange', hidden);
    doc.defaultView?.addEventListener('pagehide', stop);
    removeListeners.current = () => {
      element.removeEventListener('play', claimAudio);
      element.removeEventListener('playing', claimAudio);
      element.removeEventListener('volumechange', claimAudio);
      element.removeEventListener('pause', paused);
      doc.removeEventListener('visibilitychange', hidden);
      doc.defaultView?.removeEventListener('pagehide', stop);
    };
  }, [isPlaybackAllowed, stop]);

  useLayoutEffect(() => {
    if (!isPlaybackAllowed()) stop();
  }, [active, isPlaybackAllowed, stop]);

  return { video, videoRef, autoPlayAllowed, playbackRevision, isPlaybackAllowed };
}
